import * as React from "react";
import {
  AlertCircle,
  CheckCircle2,
  FileClock,
  Globe,
  History,
  Info,
  Loader2,
  MousePointerClick,
  RotateCcw,
  type LucideIcon,
} from "lucide-react";
import { JsonView, darkStyles } from "react-json-view-lite";
import "react-json-view-lite/dist/index.css";
import { Modal } from "../../../ui/components/Modal";
import { Button } from "../../../ui/components/Button";
import { Text } from "../../../ui/components/Text";
import { Callout } from "../../../ui/components/Callout";
import { CollapsibleSection } from "../../../ui/components/CollapsibleSection";
import { ListView } from "../../../ui/components/ListView";
import { ListViewCard } from "../../../ui/components/ListView/components/ListViewCard";
import type { ListParams, ListResult } from "../../../ui/api/listApi.types";
import { vars } from "../../../ui/styles/theme.css";
import {
  listCheckpoints,
  previewCheckpointRestore,
  restoreCheckpoint,
  waitForJob,
} from "../../lib/api/data-transfer";
import type {
  CheckpointRestorePreview,
  DataJob,
  DataTransferEntity,
} from "../../lib/api/data-transfer.types";
import { getAuthCookie } from "../../lib/auth/session-cookie";
import { useAuth } from "../../lib/auth/hooks";
import { useI18n } from "../../lib/i18n";
import {
  ChangeSummaryChips,
  KIND_LABEL,
  changeSetColumns,
  enrichChangeRows,
  type ChangeRow,
} from "./changeSetPreview";

export type CheckpointRow = {
  id: string;
  entity: string;
  version: number;
  itemCount: number;
  label: string | null;
  createdAt: string;
};

export interface CheckpointsModalProps {
  open: boolean;
  onClose: () => void;
  entity: DataTransferEntity;
  entityLabel: string;
  onRestored?: () => void;
}

type RestorePhase =
  | "idle"
  | "starting"
  | "pending"
  | "running"
  | "done"
  | "error";

type ProgressCopy = {
  title: string;
  subtitle?: string;
  Icon: LucideIcon;
  iconColor: string;
  spin?: boolean;
};

function formatWhen(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

function sortCheckpoints(
  list: CheckpointRow[],
  sortBy: string | undefined,
  order: "asc" | "desc" | undefined,
): CheckpointRow[] {
  const key = sortBy ?? "createdAt";
  const dir = order === "asc" ? 1 : -1;
  return [...list].sort((a, b) => {
    if (key === "createdAt") {
      return (
        (new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()) *
        dir
      );
    }
    if (key === "version" || key === "itemCount") {
      const av = key === "version" ? a.version : a.itemCount;
      const bv = key === "version" ? b.version : b.itemCount;
      return (av - bv) * dir;
    }
    if (key === "label") {
      return (a.label ?? "").localeCompare(b.label ?? "") * dir;
    }
    return (
      (new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()) * dir
    );
  });
}

function progressCopy(
  phase: RestorePhase,
  errorMessage: string | null,
): ProgressCopy | null {
  switch (phase) {
    case "starting":
      return {
        title: "Starting restore…",
        subtitle:
          "Creating a restore job. A checkpoint of the current state will be saved first.",
        Icon: Loader2,
        iconColor: vars.color.info,
        spin: true,
      };
    case "pending":
      return {
        title: "Queued…",
        subtitle: "Waiting for the restore job to start.",
        Icon: FileClock,
        iconColor: vars.color.info,
      };
    case "running":
      return {
        title: "Restoring…",
        subtitle:
          "Saving a checkpoint, then applying selected changes. You can close this dialog.",
        Icon: FileClock,
        iconColor: vars.color.info,
      };
    case "done":
      return {
        title: "Restore complete",
        subtitle:
          "The collection has been restored to the selected checkpoint.",
        Icon: CheckCircle2,
        iconColor: vars.color.success,
      };
    case "error":
      return {
        title: "Restore failed",
        subtitle: errorMessage || "Something went wrong. Please try again.",
        Icon: AlertCircle,
        iconColor: vars.color.danger,
      };
    default:
      return null;
  }
}

function phaseFromJobStatus(status: DataJob["status"]): RestorePhase | null {
  if (status === "pending") return "pending";
  if (status === "running") return "running";
  if (status === "ready") return "done";
  if (status === "failed") return "error";
  return null;
}

const jsonPreviewShellStyle: React.CSSProperties = {
  borderRadius: 8,
  padding: "12px 16px",
  fontSize: 12,
  lineHeight: 1.6,
  overflowX: "auto",
  fontFamily: "monospace",
  maxHeight: 280,
  overflowY: "auto",
  background: vars.color.muted,
  border: `1px solid ${vars.color.border}`,
};

export function CheckpointsModal({
  open,
  onClose,
  entity,
  entityLabel,
  onRestored,
}: CheckpointsModalProps) {
  const { t } = useI18n();
  const { accessToken } = useAuth();
  const token = accessToken ?? getAuthCookie("access_token") ?? "";
  const [rows, setRows] = React.useState<CheckpointRow[]>([]);
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [selectedIds, setSelectedIds] = React.useState<string[]>([]);

  const [preview, setPreview] = React.useState<CheckpointRestorePreview | null>(
    null,
  );
  const [previewLoading, setPreviewLoading] = React.useState(false);
  const [previewError, setPreviewError] = React.useState<string | null>(null);
  const [changeSelectedIds, setChangeSelectedIds] = React.useState<string[]>(
    [],
  );
  const [localizationExpanded, setLocalizationExpanded] = React.useState(false);

  const [phase, setPhase] = React.useState<RestorePhase>("idle");
  const [restoreError, setRestoreError] = React.useState<string | null>(null);
  const [jobId, setJobId] = React.useState<string | null>(null);

  const reloadCheckpoints = React.useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await listCheckpoints(token, entity);
      setRows(res.checkpoints ?? []);
    } catch (e: any) {
      setError(e?.message || "Failed to load checkpoints");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [token, entity]);

  React.useEffect(() => {
    if (!open || !token) {
      setRows([]);
      setError(null);
      setSelectedIds([]);
      setPreview(null);
      setPreviewError(null);
      setChangeSelectedIds([]);
      setPhase("idle");
      setRestoreError(null);
      setJobId(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setSelectedIds([]);
    setPreview(null);
    setPreviewError(null);
    setChangeSelectedIds([]);
    setPhase("idle");
    setRestoreError(null);
    setJobId(null);
    void (async () => {
      try {
        const res = await listCheckpoints(token, entity);
        if (cancelled) return;
        setRows(res.checkpoints ?? []);
      } catch (e: any) {
        if (cancelled) return;
        setError(e?.message || "Failed to load checkpoints");
        setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, token, entity]);

  const selectedCheckpointId = selectedIds[0] ?? null;
  const inProgress =
    phase === "starting" || phase === "pending" || phase === "running";
  const showProgress = phase !== "idle";

  React.useEffect(() => {
    if (!open || !token || !selectedCheckpointId || showProgress) {
      if (!selectedCheckpointId) {
        setPreview(null);
        setPreviewError(null);
        setChangeSelectedIds([]);
        setPreviewLoading(false);
      }
      return;
    }
    let cancelled = false;
    setPreviewLoading(true);
    setPreviewError(null);
    setPreview(null);
    setChangeSelectedIds([]);
    void (async () => {
      try {
        const result = await previewCheckpointRestore(
          token,
          selectedCheckpointId,
        );
        if (cancelled) return;
        setPreview(result);
        const actionable = result.changeset.items
          .filter((i) => i.kind !== "unchanged")
          .map((i) => i.id);
        setChangeSelectedIds(actionable);
      } catch (e: any) {
        if (cancelled) return;
        setPreviewError(e?.message || "Failed to preview restore");
        setPreview(null);
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, token, selectedCheckpointId, showProgress]);

  const fetchList = React.useCallback(
    (params: ListParams): Promise<ListResult<CheckpointRow>> => {
      const filtered = params.search
        ? rows.filter((row) =>
            [row.label ?? "", `v${row.version}`, String(row.itemCount)].some(
              (v) => v.toLowerCase().includes(params.search!.toLowerCase()),
            ),
          )
        : rows;
      const sorted = sortCheckpoints(filtered, params.sort_by, params.order);
      const start = (params.page - 1) * params.limit;
      return Promise.resolve({
        data: sorted.slice(start, start + params.limit),
        total: sorted.length,
      });
    },
    [rows],
  );

  const changeRows = React.useMemo(
    () =>
      preview
        ? enrichChangeRows(preview.entity, preview.items, preview.changeset)
        : [],
    [preview],
  );

  const fetchChanges = React.useCallback(
    (params: ListParams): Promise<ListResult<ChangeRow>> => {
      const filtered = params.search
        ? changeRows.filter((i) =>
            [i.label, i.matchKey, KIND_LABEL[i.kind]].some((v) =>
              v.toLowerCase().includes(params.search!.toLowerCase()),
            ),
          )
        : changeRows;
      const start = (params.page - 1) * params.limit;
      return Promise.resolve({
        data: filtered.slice(start, start + params.limit),
        total: filtered.length,
      });
    },
    [changeRows],
  );

  const actionableChangeIds = React.useMemo(
    () =>
      (preview?.changeset.items ?? [])
        .filter((i) => i.kind !== "unchanged")
        .map((i) => i.id),
    [preview],
  );
  const allChangeIds = preview?.changeset.items.map((i) => i.id) ?? [];
  const unchangedCount = preview?.changeset.summary.unchanged ?? 0;

  const onChangeSelectionChange = React.useCallback((ids: string[]) => {
    setChangeSelectedIds(ids.filter((id) => !id.startsWith("nop:")));
  }, []);

  const getRowCanSelect = React.useCallback(
    (row: ChangeRow) => row.kind !== "unchanged",
    [],
  );

  const selectedActionableCount = changeSelectedIds.filter(
    (id) => !id.startsWith("nop:"),
  ).length;

  const selectionSummary = preview
    ? `${selectedActionableCount} of ${actionableChangeIds.length} change${
        actionableChangeIds.length === 1 ? "" : "s"
      } selected.${
        unchangedCount ? ` ${unchangedCount} unchanged.` : ""
      } Restore will overwrite the current ${entityLabel.toLowerCase()} collection to match this checkpoint.`
    : "";

  const showLocalization =
    Boolean(preview?.localization?.definitionsByLang) &&
    Object.keys(preview?.localization?.definitionsByLang ?? {}).length > 0;

  const localizationSubtitle = preview?.localization
    ? `${Object.keys(preview.localization.definitionsByLang).length} language${
        Object.keys(preview.localization.definitionsByLang).length === 1
          ? ""
          : "s"
      }`
    : undefined;

  const canRestore =
    Boolean(preview) &&
    !preview?.legacy &&
    selectedActionableCount > 0 &&
    !previewLoading &&
    !inProgress &&
    phase !== "done";

  const restorePreviewTooltip =
    "A checkpoint is a full snapshot of this collection saved before each import apply. The latest 20 are kept. Restoring overwrites the current collection to match the selected checkpoint (including deletions).";

  const copy = progressCopy(phase, restoreError);

  const onRestore = async () => {
    if (!token || !selectedCheckpointId || !changeSelectedIds.length) return;
    setRestoreError(null);
    setPhase("starting");
    setJobId(null);
    try {
      const { jobId: id } = await restoreCheckpoint(
        token,
        selectedCheckpointId,
        { changeIds: changeSelectedIds },
      );
      setJobId(id);
      setPhase("pending");
      const job = await waitForJob(token, id, {
        onUpdate: (j) => {
          const next = phaseFromJobStatus(j.status);
          if (next && next !== "done" && next !== "error") setPhase(next);
          if (j.status === "failed") {
            setRestoreError(j.error || "Restore failed");
          }
        },
      });
      if (job.status === "failed") {
        setRestoreError(job.error || "Restore failed");
        setPhase("error");
        return;
      }
      setPhase("done");
      onRestored?.();
      void reloadCheckpoints();
    } catch (e: any) {
      setRestoreError(e?.message || "Restore failed");
      setPhase("error");
    }
  };

  return (
    <Modal
      open={open}
      setOpen={(v) => {
        if (!v) onClose();
      }}
      minWidth="720px"
      maxWidth="960px"
      maxHeight="90vh"
      closeOnClickOff={!inProgress}
    >
      <Modal.Header>
        <Text level="title-small">
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <History size={18} />
            {entityLabel} checkpoints
          </span>
        </Text>
      </Modal.Header>
      <Modal.Body>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 12,
            minHeight: 280,
          }}
        >
          {showProgress && copy ? (
            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                gap: 12,
                padding: "28px 12px 16px",
              }}
            >
              <Text
                level="title-small"
                weight="medium"
                color={phase === "error" ? "danger" : undefined}
              >
                {copy.title}
              </Text>
              <copy.Icon
                size={36}
                strokeWidth={1.75}
                style={{
                  color: copy.iconColor,
                  flexShrink: 0,
                  ...(copy.spin
                    ? {
                        animation:
                          "checkpoint-restore-spin 0.9s linear infinite",
                      }
                    : null),
                }}
              />
              {copy.subtitle && (
                <Text level="body-small" color="muted">
                  {copy.subtitle}
                </Text>
              )}
            </div>
          ) : (
            <>
              {error ? (
                <Text level="body-small" color="danger">
                  {error}
                </Text>
              ) : null}

              <div style={{ flex: 1, minHeight: 180 }}>
                <ListView<CheckpointRow>
                  listId={`checkpoints-${entity}`}
                  columns={[
                    {
                      id: "version",
                      label: "Version",
                      sortable: true,
                      width: 96,
                      render: (row) => `v${row.version}`,
                    },
                    {
                      id: "label",
                      label: "Label",
                      sortable: true,
                      render: (row) => row.label || "—",
                    },
                    {
                      id: "itemCount",
                      label: "Items",
                      sortable: true,
                      width: 88,
                      render: (row) => String(row.itemCount),
                    },
                    {
                      id: "createdAt",
                      label: "Created",
                      sortable: true,
                      render: (row) => formatWhen(row.createdAt),
                    },
                  ]}
                  fetchList={fetchList}
                  enabledViews={["stack"]}
                  compact
                  enableSelection
                  singleSelect
                  onSelectionChange={setSelectedIds}
                  renderStackCard={(row, ctx) => (
                    <ListViewCard
                      id={row.id}
                      layout="stack"
                      imageFormat="cover"
                      compact={ctx.compact}
                      image={<History size={20} style={{ opacity: 0.55 }} />}
                      title={row.label || `Checkpoint v${row.version}`}
                      badges={[
                        { id: "version", label: `v${row.version}` },
                        {
                          id: "items",
                          label: `${row.itemCount} item${row.itemCount === 1 ? "" : "s"}`,
                        },
                        { id: "when", label: formatWhen(row.createdAt) },
                      ]}
                      variant="static"
                    />
                  )}
                  disableLocalPersistence
                  contextKey={`${entity}:${rows.length}:${loading ? "loading" : "ready"}`}
                  queryOptions={{ staleTime: Infinity }}
                  emptyTitle={
                    loading ? "Loading checkpoints…" : "No checkpoints"
                  }
                  emptySubtitle={
                    loading
                      ? "Fetching saved checkpoints."
                      : "Checkpoints appear here after an import is applied."
                  }
                  searchPlaceholder="Search checkpoints…"
                  defaultPageSize={10}
                  defaultSort={{ sort_by: "createdAt", order: "desc" }}
                />
              </div>

              {!selectedCheckpointId ? (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 8,
                    padding: "20px 12px",
                    textAlign: "center",
                  }}
                >
                  <MousePointerClick
                    size={22}
                    strokeWidth={1.75}
                    style={{ color: vars.color.fgMuted, opacity: 0.85 }}
                  />
                  <Text level="body-small" color="muted">
                    Select a checkpoint to preview restore changes.
                  </Text>
                </div>
              ) : (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 12,
                    borderTop: `1px solid ${vars.color.border}`,
                    paddingTop: 12,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 12,
                    }}
                  >
                    <Text level="title-small">{t("pro.dataTransfer.restorePreview")}</Text>
                    <button
                      type="button"
                      aria-label={restorePreviewTooltip}
                      title={restorePreviewTooltip}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 28,
                        height: 28,
                        border: "none",
                        borderRadius: 6,
                        background: "transparent",
                        color: vars.color.fgMuted,
                        cursor: "help",
                        padding: 0,
                        flexShrink: 0,
                      }}
                    >
                      <Info size={16} strokeWidth={2} />
                    </button>
                  </div>

                  {previewLoading ? (
                    <Text level="body-small" color="muted">
                      Computing diff against current{" "}
                      {entityLabel.toLowerCase()}…
                    </Text>
                  ) : null}

                  {previewError ? (
                    <Text level="body-small" color="danger">
                      {previewError}
                    </Text>
                  ) : null}

                  {preview?.legacy ? (
                    <Callout variant="info">
                      This checkpoint was saved before full collection
                      snapshots. Restore preview is unavailable — apply a new
                      import to create a restorable checkpoint.
                    </Callout>
                  ) : null}

                  {preview && !preview.legacy ? (
                    <>
                      <div style={{ minHeight: 220 }}>
                        <ListView<ChangeRow>
                          listId={`checkpoint-restore-${preview.checkpoint.id}`}
                          columns={changeSetColumns}
                          fetchList={fetchChanges}
                          enabledViews={["table"]}
                          enableSelection
                          getRowCanSelect={getRowCanSelect}
                          defaultSelectedIds={actionableChangeIds}
                          onSelectionChange={onChangeSelectionChange}
                          disableLocalPersistence
                          contextKey={`${preview.checkpoint.id}:${allChangeIds.length}:${changeRows.filter((r) => r.imageUrl).length}`}
                          queryOptions={{ staleTime: Infinity }}
                          emptyTitle="No changes"
                          emptySubtitle={t("pro.dataTransfer.checkpointMatches")}
                          defaultPageSize={10}
                        />
                        <div style={{ marginTop: 10 }}>
                          <Callout variant="info">{selectionSummary}</Callout>
                        </div>
                      </div>

                      <ChangeSummaryChips summary={preview.changeset.summary} />

                      {showLocalization && preview.localization ? (
                        <CollapsibleSection
                          id="checkpoint-restore-localization"
                          title={t("pro.system.localization.title")}
                          subtitle={localizationSubtitle}
                          icon={<Globe size={16} />}
                          expanded={localizationExpanded}
                          onToggleExpanded={() =>
                            setLocalizationExpanded((v) => !v)
                          }
                          compactHeader
                        >
                          <div style={jsonPreviewShellStyle}>
                            <JsonView
                              data={preview.localization}
                              style={darkStyles}
                              shouldExpandNode={(level: number) => level < 2}
                            />
                          </div>
                        </CollapsibleSection>
                      ) : null}
                    </>
                  ) : null}
                </div>
              )}
            </>
          )}
        </div>
        {showProgress && phase === "starting" ? (
          <style>{`@keyframes checkpoint-restore-spin { to { transform: rotate(360deg); } }`}</style>
        ) : null}
      </Modal.Body>
      <Modal.Footer style={{ justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", minWidth: 28 }}>
          {showProgress && jobId ? (
            <span
              title={`Job ${jobId}`}
              aria-label={`Job ${jobId}`}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                color: vars.color.fgMuted,
                cursor: "default",
              }}
            >
              <Info size={16} strokeWidth={2} />
            </span>
          ) : null}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Button variant="ghost" color="neutral" onClick={onClose}>
            {showProgress ? "Close" : "Cancel"}
          </Button>
          {phase === "error" ? (
            <Button
              color="primary"
              onClick={() => void onRestore()}
              iconLeft={<RotateCcw size={16} />}
            >
              Try again
            </Button>
          ) : phase === "done" ? (
            <Button color="primary" onClick={onClose}>
              Done
            </Button>
          ) : (
            <Button
              variant="solid"
              color="primary"
              disabled={!canRestore}
              onClick={() => void onRestore()}
              title={
                canRestore
                  ? "Restore selected changes from this checkpoint"
                  : "Select a checkpoint with changes to restore"
              }
            >
              <span
                style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
              >
                <RotateCcw size={16} />
                Restore checkpoint
              </span>
            </Button>
          )}
        </div>
      </Modal.Footer>
    </Modal>
  );
}
